/**
 * Product Builder for coffrets
 * Manages 3-step builder: variant selection, cover color, flavor composition
 */

class ProductBuilder {
  constructor(container) {
    // Use the form container as scope so we can find all builder steps (siblings)
    this.container = container.closest('.product-form__container') || container.parentElement;
    this.builderEl = container;
    this.selectedVariantId = null;
    this.selectedVariantTitle = '';
    this.selectedCoverColor = '';
    this.selectedCoverVariantId = null;
    this.hasCoverProduct = false;
    this.maxFlavors = 0;
    this.currentFlavorCount = 0;
    this.flavors = {};

    this.init();
  }

  init() {
    this.initCoverStep();
    this.initFlavorsStep();
    // Variant step last so selectVariant() can trigger cover visibility + flavors max
    this.initVariantStep();
    this.recalculateStepNumbers();
    this.updateSubmitButtons();
  }

  // ===========================
  // Step 1: Variant Selection
  // ===========================

  initVariantStep() {
    const variantCards = this.container.querySelectorAll('[data-builder-variant-card]');
    if (!variantCards.length) return;

    variantCards.forEach(card => {
      card.addEventListener('click', () => this.selectVariant(card));
    });

    // Read Liquid-initialized state from the already-active card
    const activeCard = this.container.querySelector('.builder-variant__card--active');
    if (activeCard) {
      this.selectedVariantId = Number(activeCard.dataset.variantId);
      this.selectedVariantTitle = activeCard.dataset.variantTitle;
      const match = this.selectedVariantTitle.match(/(\d+)/);
      this.maxFlavors = match ? parseInt(match[1], 10) : 0;

      // Update hidden form input
      const formInput = this.container.closest('product-template')?.querySelector('input[name="id"]');
      if (formInput) formInput.value = this.selectedVariantId;

      // Cover visibility is already set by Liquid, just sync JS state
      this.updateCoverVisibility();
      this.updateAllPlusButtons();
      this.updateFlavorsCounter();
    this.updateSubmitButtons();
    }
  }

  selectVariant(card) {
    // Deselect all
    this.container.querySelectorAll('[data-builder-variant-card]').forEach(c => {
      c.classList.remove('builder-variant__card--active');
    });

    // Select this one
    card.classList.add('builder-variant__card--active');

    this.selectedVariantId = Number(card.dataset.variantId);
    this.selectedVariantTitle = card.dataset.variantTitle;

    // Parse max flavors from variant title (first number found)
    const match = this.selectedVariantTitle.match(/(\d+)/);
    this.maxFlavors = match ? parseInt(match[1], 10) : 0;

    // Update price display
    const price = Number(card.dataset.variantPrice);
    const comparePrice = Number(card.dataset.variantComparePrice);
    this.updatePriceDisplay(price, comparePrice);

    // Update hidden form input
    const formInput = this.container.closest('product-template')?.querySelector('input[name="id"]');
    if (formInput) {
      formInput.value = this.selectedVariantId;
    }

    // Update cover step visibility
    this.updateCoverVisibility();

    // Update flavors max
    this.updateFlavorsMax();

    // Dispatch event for other components
    this.container.dispatchEvent(new CustomEvent('builder:variant-changed', {
      bubbles: true,
      detail: { variantId: this.selectedVariantId, variantTitle: this.selectedVariantTitle, maxFlavors: this.maxFlavors }
    }));
  }

  updatePriceDisplay(price, comparePrice) {
    const productTemplate = this.container.closest('product-template');
    if (!productTemplate) return;

    const priceEl = productTemplate.querySelector('[variant-price-change-unit]');
    if (priceEl) {
      const formattedPrice = this.formatMoney(price);
      let html = formattedPrice;
      if (comparePrice && comparePrice > price) {
        html += ` <span class="product-form__title-price--compare">${this.formatMoney(comparePrice)}</span>`;
      }
      priceEl.innerHTML = html;
    }

    // Update base price for button
    const basePriceEl = productTemplate.querySelector('.product-info__price[data-base-price]');
    if (basePriceEl) {
      basePriceEl.setAttribute('data-base-price', price);
      basePriceEl.textContent = this.formatMoney(price);
    }
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + '€';
  }

  // ===========================
  // Step 2: Cover Selection
  // ===========================

  initCoverStep() {
    this.coverSwipers = new Map();

    const coverSteps = this.container.querySelectorAll('[data-builder-cover]');
    coverSteps.forEach(step => {
      if (step.hasAttribute('data-builder-cover-product')) {
        this.hasCoverProduct = true;
      }
    });

    const coverCards = this.container.querySelectorAll('[data-builder-cover-card]');
    if (!coverCards.length) return;

    coverCards.forEach(card => {
      if (!card.disabled) {
        card.addEventListener('click', () => this.selectCover(card));
      }
    });

    // Auto-select first available card of each visible cover step
    const visibleStep = this.container.querySelector('.builder-cover--visible');
    if (visibleStep) {
      const firstCard = visibleStep.querySelector('[data-builder-cover-card]:not([disabled])');
      if (firstCard) this.selectCover(firstCard);
      requestAnimationFrame(() => this.ensureCoverSwiper(visibleStep));
    }
  }

  ensureCoverSwiper(coverStep) {
    if (!coverStep) return;
    if (typeof Swiper === 'undefined') return;
    if (!window.matchMedia('(max-width: 989px)').matches) return;

    // Already initialized for this step
    if (this.coverSwipers.has(coverStep)) {
      this.coverSwipers.get(coverStep).update();
      return;
    }

    const swiperEl = coverStep.querySelector('[data-builder-cover-swiper]');
    if (!swiperEl) return;

    const wrapper = coverStep.querySelector('.builder-cover__cards-wrapper');
    const prevBtn = wrapper ? wrapper.querySelector('.builder-cover__swiper-prev') : null;
    const nextBtn = wrapper ? wrapper.querySelector('.builder-cover__swiper-next') : null;
    const pagination = coverStep.querySelector('.builder-cover__swiper-pagination');

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 6,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      pagination: {
        el: pagination,
        clickable: true,
      },
    });

    this.coverSwipers.set(coverStep, swiper);
  }

  selectCover(card) {
    if (card.disabled) return;
    // Only deselect cards within the same cover step
    const coverStep = card.closest('[data-builder-cover]');
    if (coverStep) {
      coverStep.querySelectorAll('[data-builder-cover-card]').forEach(c => {
        c.classList.remove('builder-cover__card--active');
      });
    }

    card.classList.add('builder-cover__card--active');
    this.selectedCoverColor = card.dataset.coverTitle;
    this.selectedCoverVariantId = card.dataset.coverVariantId ? Number(card.dataset.coverVariantId) : null;
  }

  updateCoverVisibility() {
    const coverSteps = this.container.querySelectorAll('[data-builder-cover]');
    if (!coverSteps.length) return;

    // Reset cover selection — will be re-set by the visible step
    this.selectedCoverColor = '';
    this.selectedCoverVariantId = null;

    coverSteps.forEach(coverStep => {
      const linkedVariant = coverStep.dataset.builderCoverLinked || '';
      if (linkedVariant && this.selectedVariantTitle.toLowerCase().includes(linkedVariant.toLowerCase())) {
        coverStep.classList.add('builder-cover--visible');
        // Init or update Swiper now that cover is visible
        requestAnimationFrame(() => {
          this.ensureCoverSwiper(coverStep);
        });
        // Auto-select first available card of this visible cover step
        const activeCard = coverStep.querySelector('.builder-cover__card--active:not([disabled])') || coverStep.querySelector('[data-builder-cover-card]:not([disabled])');
        if (activeCard) {
          this.selectCover(activeCard);
        }
      } else {
        coverStep.classList.remove('builder-cover--visible');
      }
    });

    this.recalculateStepNumbers();
  }

  /**
   * Recalculate step numbers for all visible builder steps.
   * Hidden steps (e.g. cover when wrong variant) are skipped.
   */
  recalculateStepNumbers() {
    const allSteps = this.container.querySelectorAll('[data-builder-step]');
    let stepNum = 0;

    allSteps.forEach(step => {
      // Skip hidden steps (builder-cover not visible)
      const isHidden = step.classList.contains('builder-cover') && !step.classList.contains('builder-cover--visible');
      if (isHidden) return;

      stepNum++;
      const numberEl = step.querySelector('[data-builder-step-number]');
      if (numberEl) {
        numberEl.textContent = stepNum;
      }
    });
  }

  // ===========================
  // Step 3: Flavors
  // ===========================

  initFlavorsStep() {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) return;

    // Qty buttons
    flavorsContainer.querySelectorAll('[data-flavor-plus]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = btn.closest('[data-builder-flavor-card]');
        this.changeFlavorQty(card, 1);
      });
    });

    flavorsContainer.querySelectorAll('[data-flavor-minus]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = btn.closest('[data-builder-flavor-card]');
        this.changeFlavorQty(card, -1);
      });
    });

    // Filters
    flavorsContainer.querySelectorAll('[data-flavor-filter]').forEach(tab => {
      tab.addEventListener('click', () => this.filterFlavors(tab));
    });

    // Progress bar = scroll indicator for filter tabs
    const filterTabs = flavorsContainer.querySelector('.builder-flavors__filter-tabs');
    if (filterTabs) {
      filterTabs.addEventListener('scroll', () => this.updateProgressBar());
      this.updateProgressBar();
    }

    // Detail links
    flavorsContainer.querySelectorAll('.builder-flavor__details').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = link.dataset.productUrl;
        const productTemplate = this.container.closest('product-template');
        if (productTemplate && productTemplate.openProductModal) {
          productTemplate.openProductModal(url);
        }
      });
    });

    // Desktop: scroll message on list scroll
    // Mobile: "Voir plus" to reveal hidden cards
    this.initScrollOrShowMore(flavorsContainer);
  }

  initScrollOrShowMore(flavorsContainer) {
    const list = flavorsContainer.querySelector('.builder-flavors__list');
    const msg = flavorsContainer.querySelector('.builder-flavors__scroll-msg');
    if (!list || !msg) return;

    // Start hidden
    msg.classList.add('builder-flavors__scroll-msg--hidden');

    const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

    if (isMobile()) {
      // Mobile: hide cards beyond 6, show "Voir plus"
      this.applyMobileCardLimit(flavorsContainer);
      msg.addEventListener('click', () => {
        this.animateListChange(() => {
          flavorsContainer.querySelectorAll('.builder-flavor__card--mobile-hidden').forEach(card => {
            card.classList.remove('builder-flavor__card--mobile-hidden');
          });
        });
        this.hideScrollMsg(msg);
      });
    } else {
      // Desktop: scroll-based message
      this.checkScrollMessage();
      list.addEventListener('scroll', () => this.checkScrollMessage());
    }
  }

  showScrollMsg(msg) {
    msg.classList.remove('builder-flavors__scroll-msg--hidden');
  }

  hideScrollMsg(msg) {
    msg.classList.add('builder-flavors__scroll-msg--hidden');
  }

  /**
   * Capture current list height, run a callback that changes content,
   * then animate smoothly to the new height.
   */
  animateListChange(callback) {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) { callback(); return; }

    const list = flavorsContainer.querySelector('.builder-flavors__list');
    if (!list) { callback(); return; }

    const prevHeight = list.offsetHeight;

    callback();

    requestAnimationFrame(() => {
      const newHeight = list.scrollHeight;
      const maxH = window.matchMedia('(max-width: 989px)').matches ? newHeight : Math.min(newHeight, 442);

      if (prevHeight === maxH) return;

      list.style.transition = 'none';
      list.style.maxHeight = prevHeight + 'px';
      list.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        list.style.transition = 'max-height 0.35s ease';
        list.style.maxHeight = maxH + 'px';

        const onEnd = () => {
          list.style.maxHeight = '';
          list.style.overflow = '';
          list.style.transition = '';
          list.removeEventListener('transitionend', onEnd);
        };
        list.addEventListener('transitionend', onEnd);
      });
    });
  }

  applyMobileCardLimit(flavorsContainer) {
    const cards = flavorsContainer.querySelectorAll('[data-builder-flavor-card]');
    const MOBILE_LIMIT = 6;
    let visibleCount = 0;

    cards.forEach(card => {
      if (card.classList.contains('builder-flavor__card--hidden')) return; // filtered out
      visibleCount++;
      if (visibleCount > MOBILE_LIMIT) {
        card.classList.add('builder-flavor__card--mobile-hidden');
      }
    });

    // Show/hide "Voir plus" based on hidden cards count
    const msg = flavorsContainer.querySelector('.builder-flavors__scroll-msg');
    if (msg) {
      if (visibleCount > MOBILE_LIMIT) {
        this.showScrollMsg(msg);
      } else {
        this.hideScrollMsg(msg);
      }
    }
  }

  getFlavorMaxQty(card) {
    const available = card.dataset.flavorAvailable === 'true';
    if (!available) return 0;

    const policy = card.dataset.flavorInventoryPolicy;
    if (policy === 'continue') return Infinity;

    const inventory = parseInt(card.dataset.flavorInventory, 10) || 0;
    return Math.max(0, inventory);
  }

  changeFlavorQty(card, delta) {
    const id = card.dataset.flavorId;
    const name = card.dataset.flavorName;
    let qty = parseInt(card.dataset.flavorQty, 10) || 0;
    const newQty = qty + delta;
    const maxQty = this.getFlavorMaxQty(card);

    if (newQty < 0) return;
    if (delta > 0 && this.currentFlavorCount >= this.maxFlavors) return;
    if (newQty > maxQty) return;

    card.dataset.flavorQty = newQty;
    this.currentFlavorCount += delta;

    // Update display
    const display = card.querySelector('[data-flavor-qty-display]');
    if (display) display.textContent = newQty;

    // Update button states
    const minusBtn = card.querySelector('[data-flavor-minus]');
    const plusBtn = card.querySelector('[data-flavor-plus]');
    if (minusBtn) minusBtn.disabled = newQty <= 0;
    if (plusBtn) plusBtn.disabled = this.currentFlavorCount >= this.maxFlavors || newQty >= maxQty;

    // Update card active state
    if (newQty > 0) {
      card.classList.add('builder-flavor__card--active');
    } else {
      card.classList.remove('builder-flavor__card--active');
    }

    // Store flavor data
    if (newQty > 0) {
      this.flavors[id] = { name, qty: newQty };
    } else {
      delete this.flavors[id];
    }

    // Disable all plus buttons if max reached
    this.updateAllPlusButtons();

    // Update UI
    this.updateFlavorsCounter();
    this.updateSubmitButtons();
    this.updateFlavorsSummary();
  }

  updateAllPlusButtons() {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) return;

    flavorsContainer.querySelectorAll('[data-flavor-plus]').forEach(btn => {
      const card = btn.closest('[data-builder-flavor-card]');
      const qty = parseInt(card.dataset.flavorQty, 10) || 0;
      const maxQty = this.getFlavorMaxQty(card);
      btn.disabled = this.currentFlavorCount >= this.maxFlavors || qty >= maxQty;
    });
  }

  updateFlavorsMax() {
    // Reset all flavors
    this.currentFlavorCount = 0;
    this.flavors = {};

    this.container.querySelectorAll('[data-builder-flavor-card]').forEach(card => {
      card.dataset.flavorQty = '0';
      card.classList.remove('builder-flavor__card--active');
      const display = card.querySelector('[data-flavor-qty-display]');
      if (display) display.textContent = '0';
      const minusBtn = card.querySelector('[data-flavor-minus]');
      if (minusBtn) minusBtn.disabled = true;
    });

    this.updateAllPlusButtons();
    this.updateFlavorsCounter();
    this.updateSubmitButtons();
    this.updateFlavorsSummary();
  }

  updateFlavorsCounter() {
    const counter = this.container.querySelector('[data-builder-flavors-counter]');
    if (counter) {
      counter.textContent = `${this.currentFlavorCount}/${this.maxFlavors}`;
    }

    const summaryCounter = this.container.querySelector('[data-builder-summary-counter]');
    if (summaryCounter) {
      summaryCounter.textContent = `${this.currentFlavorCount}/${this.maxFlavors}`;
    }
  }

  updateProgressBar() {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) return;

    const tabs = flavorsContainer.querySelector('.builder-flavors__filter-tabs');
    const fill = flavorsContainer.querySelector('.builder-flavors__progress-fill');
    if (!tabs || !fill) return;

    const scrollLeft = tabs.scrollLeft;
    const scrollWidth = tabs.scrollWidth;
    const clientWidth = tabs.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) {
      // All tabs visible, fill 100%
      fill.style.width = '100%';
    } else {
      // Thumb size = visible portion ratio
      const thumbRatio = clientWidth / scrollWidth;
      const scrollRatio = scrollLeft / maxScroll;
      const thumbWidth = thumbRatio * 100;
      const thumbLeft = scrollRatio * (100 - thumbWidth);
      fill.style.width = thumbWidth + '%';
      fill.style.marginLeft = thumbLeft + '%';
    }
  }

  updateFlavorsSummary() {
    const tagsContainer = this.container.querySelector('.builder-flavors__summary-tags');
    const emptyMsg = this.container.querySelector('.builder-flavors__summary-empty');
    if (!tagsContainer) return;

    tagsContainer.innerHTML = '';
    const entries = Object.entries(this.flavors);

    if (entries.length === 0) {
      if (emptyMsg) emptyMsg.classList.add('builder-flavors__summary-empty--visible');
      return;
    }

    if (emptyMsg) emptyMsg.classList.remove('builder-flavors__summary-empty--visible');

    entries.forEach(([id, data]) => {
      const tag = document.createElement('span');
      tag.className = 'builder-flavors__summary-tag pxs';
      tag.textContent = `${data.qty} x ${data.name}`;
      tagsContainer.appendChild(tag);
    });
  }

  filterFlavors(activeTab) {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) return;

    // Update tab states
    flavorsContainer.querySelectorAll('[data-flavor-filter]').forEach(tab => {
      tab.classList.remove('builder-flavors__filter-tab--active');
    });
    activeTab.classList.add('builder-flavors__filter-tab--active');

    const category = activeTab.dataset.flavorFilter;

    this.animateListChange(() => {
      flavorsContainer.querySelectorAll('[data-builder-flavor-card]').forEach(card => {
        card.classList.remove('builder-flavor__card--mobile-hidden');
        if (category === 'all' || card.dataset.flavorCategory === category) {
          card.classList.remove('builder-flavor__card--hidden');
        } else {
          card.classList.add('builder-flavor__card--hidden');
        }
      });

      // Re-apply mobile card limit after filter
      if (window.matchMedia('(max-width: 989px)').matches) {
        this.applyMobileCardLimit(flavorsContainer);
      }
    });

    // Update scroll message after animation
    if (!window.matchMedia('(max-width: 989px)').matches) {
      setTimeout(() => this.checkScrollMessage(), 400);
    }
  }

  checkScrollMessage() {
    const flavorsContainer = this.container.querySelector('[data-builder-flavors]');
    if (!flavorsContainer) return;

    const list = flavorsContainer.querySelector('.builder-flavors__list');
    const msg = flavorsContainer.querySelector('.builder-flavors__scroll-msg');
    if (!list || !msg) return;

    const isScrollable = list.scrollHeight > list.clientHeight;
    const isAtBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 10;

    if (isScrollable && !isAtBottom) {
      this.showScrollMsg(msg);
    } else {
      this.hideScrollMsg(msg);
    }
  }

  // ===========================
  // Submit Button State
  // ===========================

  updateSubmitButtons() {
    const productTemplate = this.container.closest('product-template');
    if (!productTemplate) return;

    const isComplete = this.currentFlavorCount === this.maxFlavors && this.maxFlavors > 0;
    const remaining = this.maxFlavors - this.currentFlavorCount;

    // Main submit button
    const mainBtn = productTemplate.querySelector('.product-info__add-button button[type="submit"]');
    if (mainBtn) {
      mainBtn.disabled = !isComplete;
      this.updateButtonText(mainBtn, isComplete, remaining);
    }

    // Sticky submit button
    const stickyBtn = productTemplate.querySelector('.product-form__sticky-submit-button button');
    if (stickyBtn) {
      stickyBtn.disabled = !isComplete;
      this.updateStickyButtonText(stickyBtn, isComplete, remaining);
    }
  }

  getRemainingMessage(btn, remaining) {
    const template = btn.dataset.builderRemainingText || 'Encore {count} tablettes à sélectionner';
    return template.replace('{count}', remaining);
  }

  updateButtonText(btn, isComplete, remaining) {
    const mobileSpan = btn.querySelector('.only-mobile');
    const desktopSpan = btn.querySelector('.only-desktop');
    const priceSpan = btn.querySelector('.product-info__price');

    if (isComplete) {
      const addText = btn.dataset.addToCartText || 'Ajouter au panier •';
      if (mobileSpan) mobileSpan.textContent = 'Ajouter •';
      if (desktopSpan) desktopSpan.textContent = addText;
      if (priceSpan) priceSpan.style.display = '';
    } else {
      const msg = this.getRemainingMessage(btn, remaining);
      if (mobileSpan) mobileSpan.textContent = msg;
      if (desktopSpan) desktopSpan.textContent = msg;
      if (priceSpan) priceSpan.style.display = 'none';
    }
  }

  updateStickyButtonText(btn, isComplete, remaining) {
    if (!btn._originalHTML) {
      btn._originalHTML = btn.innerHTML;
    }
    if (isComplete) {
      btn.innerHTML = btn._originalHTML;
    } else {
      // Read the template from the main submit button
      const productTemplate = this.container.closest('product-template');
      const mainBtn = productTemplate ? productTemplate.querySelector('.product-info__add-button button[type="submit"]') : null;
      const msg = this.getRemainingMessage(mainBtn || btn, remaining);
      btn.textContent = msg;
    }
  }

  // ===========================
  // Data Collection
  // ===========================

  getBuilderData() {
    const entries = Object.entries(this.flavors);
    const contentParts = entries.map(([id, data]) => `${data.qty} x ${data.name}`);
    const idsParts = entries.map(([id, data]) => `${id}:${data.qty}`);

    return {
      variantId: this.selectedVariantId,
      coverColor: this.selectedCoverColor,
      coverVariantId: this.selectedCoverVariantId,
      flavors: entries,
      contentString: contentParts.join(', '),
      idsString: idsParts.join(', '),
      isComplete: this.currentFlavorCount === this.maxFlavors && this.maxFlavors > 0
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-builder]').forEach(container => {
    container._builder = new ProductBuilder(container);
  });
});
